

import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase-client';
import { calculateAggregatedMetrics } from '@/lib/profile-metrics';
import { useAuth } from '@/context/AuthContext';
import RecruiterDashboard from './RecruiterDashboard';

ChartJS.register(...registerables);

interface DashboardInsights {
  kpis: {
    lastScore: number;
    previousScore: number | null;
    overallImprovement: number | null;
    avgFinal: number;
    avgContent: number;
    avgBehaviour: number;
  };
  charts: {
    lastVsPrevious: {
      labels: string[];
      last: number[];
      previous: number[] | null;
    };
    scoreTrend: {
      labels: string[];
      values: number[];
    };
  };
  feedback: {
    headline: string;
    bullets: string[];
    targets: string[];
  };
  recentSessions: Array<{
    id: string;
    jobTitle: string;
    company: string;
    date: string;
    duration: string;
    finalScore: number;
    improvement: number | null;
    communication: number;
    confidence: number;
  }>;
}

function DashboardPage() {
  const navigate = useNavigate();
  const [insights, setInsights] = useState<DashboardInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const scoreTrendChartRef = useRef<HTMLCanvasElement>(null);
  const lastVsPreviousChartRef = useRef<HTMLCanvasElement>(null);
  const scoreTrendChartInstance = useRef<ChartJS | null>(null);
  const lastVsPreviousChartInstance = useRef<ChartJS | null>(null);
  const chartsInitialized = useRef(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const initializeCharts = () => {
    if (!insights) return;

    if (scoreTrendChartRef.current) {
      scoreTrendChartInstance.current?.destroy();
      const ctx = scoreTrendChartRef.current.getContext('2d');
      if (ctx) {
        const gradient = ctx.createLinearGradient(0, 0, 0, 260);
        gradient.addColorStop(0, 'rgba(16,185,129,0.25)');
        gradient.addColorStop(1, 'rgba(16,185,129,0)');

        scoreTrendChartInstance.current = new ChartJS(ctx, {
          type: 'line',
          data: {
            labels: insights.charts.scoreTrend.labels,
            datasets: [{
              label: 'Final Score',
              data: insights.charts.scoreTrend.values,
              tension: 0.35,
              fill: true,
              backgroundColor: gradient,
              borderColor: 'rgb(16,185,129)',
              pointRadius: 3,
              pointBackgroundColor: 'white',
              pointBorderWidth: 2,
              pointBorderColor: 'rgb(16,185,129)'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => `Score: ${ctx.parsed.y}/100`
                }
              }
            },
            scales: {
              y: {
                min: 0,
                max: 100,
                ticks: { stepSize: 20 },
                grid: { color: 'rgba(148,163,184,0.15)' }
              },
              x: {
                grid: { display: false }
              }
            }
          }
        });
      }
    }

    if (lastVsPreviousChartRef.current && insights.charts.lastVsPrevious.previous) {
      lastVsPreviousChartInstance.current?.destroy();
      const ctx = lastVsPreviousChartRef.current.getContext('2d');
      if (ctx) {
        lastVsPreviousChartInstance.current = new ChartJS(ctx, {
          type: 'bar',
          data: {
            labels: insights.charts.lastVsPrevious.labels,
            datasets: [
              {
                label: 'Last',
                data: insights.charts.lastVsPrevious.last,
                borderRadius: 6,
                backgroundColor: 'rgba(16,185,129,0.8)'
              },
              {
                label: 'Previous',
                data: insights.charts.lastVsPrevious.previous,
                borderRadius: 6,
                backgroundColor: 'rgba(148,163,184,0.6)'
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: 'bottom' as const }
            },
            scales: {
              y: {
                min: 0,
                max: 100,
                ticks: { stepSize: 20 },
                grid: { color: 'rgba(148,163,184,0.15)' }
              },
              x: {
                grid: { display: false }
              }
            }
          }
        });
      }
    }
  };

  useEffect(() => {
    if (insights && !chartsInitialized.current) {
      chartsInitialized.current = true;
      setTimeout(() => {
        initializeCharts();
      }, 100);
    }
    return () => {
      if (chartsInitialized.current) {
        scoreTrendChartInstance.current?.destroy();
        lastVsPreviousChartInstance.current?.destroy();
        scoreTrendChartInstance.current = null;
        lastVsPreviousChartInstance.current = null;
        chartsInitialized.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insights]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setLoading(false);
        return;
      }

      // Fetch completed sessions with job info
      const { data: sessions, error: sessionsError } = await supabase
        .from('interview_sessions')
        .select(`
          *,
          job_posting:job_postings(id, title, company_name)
        `)
        .eq('user_id', user.id)
        .eq('status', 'completed')
        .order('ended_at', { ascending: false })
        .limit(10);

      if (sessionsError) {
        throw sessionsError;
      }

      console.log('[Dashboard] Fetched sessions:', sessions);

      // If no sessions, show empty state
      if (!sessions || sessions.length === 0) {
        setInsights(null);
        setLoading(false);
        return;
      }

      // Calculate metrics using utility function
      const metrics = calculateAggregatedMetrics(sessions);

      // Create insights from sessions
      const recentSessions = sessions.map((session, index) => {
        const previousSession = index < sessions.length - 1 ? sessions[index + 1] : null;
        const currentScore = session.ai_evaluation?.overall_score || session.ai_evaluation?.finalScore || 0;
        const previousScore = previousSession?.ai_evaluation?.overall_score || previousSession?.ai_evaluation?.finalScore || null;
        const improvement = previousScore ? currentScore - previousScore : null;

        return {
          id: session.id,
          jobTitle: session.job_posting?.title || 'Interview',
          company: session.job_posting?.company_name || 'Company',
          date: new Date(session.ended_at || session.created_at).toLocaleDateString(),
          duration: `${Math.floor((session.duration_seconds || 0) / 60)}m`,
          finalScore: currentScore,
          improvement,
          communication: session.ai_evaluation?.communication || 0,
          confidence: session.ai_evaluation?.confidence || 0,
        };
      });

      // Calculate last vs previous comparison
      const lastSession = sessions[0];
      const previousSession = sessions.length > 1 ? sessions[1] : null;
      const lastContent = lastSession.ai_evaluation?.technicalKnowledge || 0;
      const lastBehaviour = lastSession.ai_evaluation?.confidence || 0;
      const prevContent = previousSession?.ai_evaluation?.technicalKnowledge || null;
      const prevBehaviour = previousSession?.ai_evaluation?.confidence || null;

      const lastScore = lastSession.ai_evaluation?.overall_score || lastSession.ai_evaluation?.finalScore || 0;
      const prevScore = previousSession?.ai_evaluation?.overall_score || previousSession?.ai_evaluation?.finalScore || null;
      const overallImprovement = prevScore ? lastScore - prevScore : null;

      const data: DashboardInsights = {
        kpis: {
          lastScore,
          previousScore: prevScore,
          overallImprovement,
          avgFinal: metrics.overallScore,
          avgContent: metrics.contentScore,
          avgBehaviour: metrics.behaviourScore,
        },
        charts: {
          lastVsPrevious: {
            labels: ['Content', 'Behaviour'],
            last: [lastContent, lastBehaviour],
            previous: prevContent && prevBehaviour ? [prevContent, prevBehaviour] : null,
          },
          scoreTrend: {
            labels: sessions.map(s => new Date(s.ended_at || s.created_at).toLocaleDateString()),
            values: sessions.map(s => s.ai_evaluation?.overall_score || s.ai_evaluation?.finalScore || 0),
          },
        },
        feedback: {
          headline: metrics.grade === 'Strong'
            ? 'Excellent progress!'
            : metrics.grade === 'Developing'
            ? 'Keep improving!'
            : 'Great start!',
          bullets: [
            `Overall readiness: ${metrics.grade}`,
            `Completed ${metrics.totalInterviews} interview${metrics.totalInterviews !== 1 ? 's' : ''}`,
            metrics.improvementPercentage > 0
              ? `${metrics.improvementPercentage}% improvement trend`
              : 'Keep practicing to see improvement'
          ],
          targets: [
            metrics.communication < 70 ? 'Improve communication clarity' : 'Maintain strong communication',
            metrics.technicalKnowledge < 70 ? 'Deepen technical knowledge' : 'Excellent technical depth',
            metrics.confidence < 70 ? 'Build more confidence' : 'Great confidence level'
          ],
        },
        recentSessions,
      };

      console.log('Dashboard data created:', data);
      setInsights(data);
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      if (err.name === 'AbortError') {
        setError('Request timed out. Please refresh the page.');
      } else {
        setError(err.message || 'Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* HEADER */}
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">My Dashboard</h1>
            <p className="text-sm text-slate-500">
              See how your practice interviews are improving over time.
            </p>
          </div>
          <button
            onClick={() => navigate('/interviews')}
            className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-slate-900 text-white text-sm shadow-sm hover:bg-slate-800">
            View Full History
          </button>
        </header>

        {/* Loading State */}
        {loading && (
          <div className="animate-pulse space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-white rounded-2xl shadow-sm"></div>
              ))}
            </div>
            <div className="h-96 bg-white rounded-2xl shadow-sm"></div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => navigate('/jobs')}>
              Start Your First Interview
            </Button>
          </div>
        )}

        {/* Empty State - No Data Yet */}
        {!insights && !loading && !error && (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <h2 className="text-xl font-semibold mb-2">Welcome to Your Dashboard</h2>
            <p className="text-slate-500 mb-6">
              Complete your first interview to see your progress and insights here.
            </p>
            <Button onClick={() => navigate('/jobs')}>
              Start Your First Interview
            </Button>
          </div>
        )}

        {/* Content - only show when insights loaded */}
        {insights && (
          <>
        {/* KPI CARDS */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Improvement */}
          <article className="bg-white rounded-2xl shadow-sm p-4 flex flex-col justify-between">
            <div>
              <p className="text-xs text-slate-400 uppercase">Overall Improvement</p>
              <p className="mt-2 text-2xl font-semibold flex items-baseline gap-1">
                {insights.kpis.overallImprovement !== null ? (
                  <>
                    {insights.kpis.overallImprovement > 0 ? '+' : ''}
                    {insights.kpis.overallImprovement}
                    <span className={`text-xs font-normal ${insights.kpis.overallImprovement >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      pts
                    </span>
                  </>
                ) : (
                  <span className="text-slate-400 text-lg">Not enough data</span>
                )}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Compared to your previous interview.
              </p>
            </div>
          </article>

          {/* Last score */}
          <article className="bg-white rounded-2xl shadow-sm p-4">
            <p className="text-xs text-slate-400 uppercase">Last Interview Score</p>
            <p className="mt-2 text-2xl font-semibold">{insights.kpis.lastScore} / 100</p>
            <p className="mt-1 text-xs text-slate-500">
              Most recent session
            </p>
            {insights.kpis.previousScore !== null && (
              <p className={`mt-3 text-xs ${insights.kpis.overallImprovement! >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {insights.kpis.overallImprovement! >= 0 ? '+' : ''}
                {insights.kpis.overallImprovement} vs previous
              </p>
            )}
          </article>

          {/* Rolling average */}
          <article className="bg-white rounded-2xl shadow-sm p-4">
            <p className="text-xs text-slate-400 uppercase">Average Score</p>
            <p className="mt-2 text-2xl font-semibold">{Math.round(insights.kpis.avgFinal)}</p>
            <p className="mt-1 text-xs text-slate-500">
              Content {Math.round(insights.kpis.avgContent)} • Behaviour {Math.round(insights.kpis.avgBehaviour)}
            </p>
            <p className="mt-3 text-xs text-amber-600">
              Aim for 75+ in your next sessions.
            </p>
          </article>
        </section>

        {/* MAIN CHARTS ROW */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Last vs Previous with AI Summary */}
          <article className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-6 flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Last vs Previous Interview</h2>
                <p className="text-xs text-slate-500">
                  See where you improved or dropped between the last two sessions.
                </p>
              </div>
              {insights.kpis.overallImprovement !== null && (
                <span className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium ${
                  insights.kpis.overallImprovement >= 0
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-red-50 text-red-700'
                }`}>
                  {insights.kpis.overallImprovement >= 0 ? '+' : ''}
                  {insights.kpis.overallImprovement} pts overall
                </span>
              )}
            </div>

            {insights.charts.lastVsPrevious.previous ? (
              <div style={{ height: '224px', position: 'relative' }}>
                <canvas ref={lastVsPreviousChartRef} style={{ height: '100%', width: '100%' }}></canvas>
              </div>
            ) : (
              <div className="h-56 flex items-center justify-center text-slate-400">
                Not enough data for comparison
              </div>
            )}

            {/* AI Summary */}
            <div className="flex flex-col gap-2 border-t pt-4">
              <h2 className="text-sm font-semibold">AI Summary</h2>
              <p className="text-[11px] text-slate-500">
                Overall changes based on your recent interviews.
              </p>
              <p className="text-xs font-medium text-emerald-600">
                "{insights.feedback.headline}"
              </p>
              <ul className="mt-1 space-y-1 text-xs text-slate-600">
                {insights.feedback.bullets.map((bullet, index) => (
                  <li key={index}>• {bullet}</li>
                ))}
              </ul>
            </div>
          </article>

          {/* Right column: Score Trend */}
          <article className="bg-white rounded-2xl shadow-sm p-6 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-sm font-semibold">Score Trend</h2>
                <p className="text-[11px] text-slate-500">Your recent sessions</p>
              </div>
            </div>
            <div style={{ height: '128px', position: 'relative' }}>
              <canvas ref={scoreTrendChartRef} style={{ height: '100%', width: '100%' }}></canvas>
            </div>
            <button
              onClick={() => {
                document.getElementById('recent-sessions')?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              }}
              className="mt-4 inline-flex items-center justify-center px-4 py-2 rounded-full bg-emerald-600 text-white text-xs font-medium shadow-sm hover:bg-emerald-700 transition-colors">
              View Recent Sessions
            </button>
          </article>
        </section>

        {/* NEXT TARGETS */}
        <section className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold mb-1">Next Targets</h2>
          <p className="text-xs text-slate-500 mb-4">
            Concrete goals based on your recent performance.
          </p>
          <ul className="text-xs text-slate-700 space-y-3 mb-4">
            {insights.feedback.targets.map((target, index) => (
              <li key={index}>• {target}</li>
            ))}
          </ul>
          <button
            onClick={() => navigate('/jobs')}
            className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-slate-900 text-white text-xs shadow-sm hover:bg-slate-800">
            Start Next Practice
          </button>
        </section>

        {/* RECENT SESSIONS LIST */}
        <section id="recent-sessions" className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold">Recent Sessions</h2>
              <p className="text-xs text-slate-500">
                A quick view of your latest interviews.
              </p>
            </div>
            <button
              onClick={() => navigate('/interviews')}
              className="text-xs text-slate-500 hover:text-slate-700">
              View all sessions →
            </button>
          </div>

          <div className="space-y-3">
            {insights.recentSessions.map((session) => (
              <article
                key={session.id}
                className="border border-slate-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-slate-300 transition-colors cursor-pointer"
                onClick={() => navigate(`/interviews/${session.id}`)}>
                <div>
                  <p className="font-medium">{session.jobTitle} — {session.company}</p>
                  <p className="text-xs text-slate-500">
                    {new Date(session.date).toLocaleDateString()} • {session.duration} • Final Score: <span className="font-semibold">{session.finalScore > 0 ? session.finalScore : 'Pending'}</span>
                  </p>
                  {session.improvement !== null && (
                    <p className={`mt-1 text-xs ${session.improvement >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {session.improvement >= 0 ? '▲' : '▼'} {session.improvement >= 0 ? '+' : ''}{session.improvement} vs last session
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-4 sm:text-right text-sm">
                  <div>
                    <p className="text-xs text-slate-400">Communication / Confidence</p>
                    <p className="font-medium">{session.communication > 0 ? session.communication : '-'} / {session.confidence > 0 ? session.confidence : '-'}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/interviews/${session.id}`);
                    }}
                    className="px-4 py-2 rounded-full bg-slate-900 text-white text-xs shadow-sm hover:bg-slate-800">
                    View Results
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
          </>
        )}
      </div>
    </div>
  );
}

export default function DashboardPageWrapper() {
  const { isRecruiterValue, loading, user, userProfile } = useAuth();

  console.log('[DashboardWrapper] 📊 Render:', {
    loading,
    isRecruiterValue,
    hasUser: !!user,
    hasProfile: !!userProfile
  });

  if (loading) {
    console.log('[DashboardWrapper] ⏳ Still loading auth...');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  console.log('[DashboardWrapper] ✅ Rendering:', isRecruiterValue ? 'RecruiterDashboard' : 'DashboardPage');
  return isRecruiterValue ? <RecruiterDashboard /> : <DashboardPage />;
}
